import { BreakpointObserver } from "@angular/cdk/layout";
import { DOCUMENT, NgTemplateOutlet, AsyncPipe } from "@angular/common";
import { AfterViewInit, Component, ElementRef, inject, Inject, Injector, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { MatSidenavContent, MatSidenavContainer, MatSidenav } from "@angular/material/sidenav";

import { combineLatest, Observable, Subscription, timer } from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

import { AktuelBrugerObserver } from "../bruger";
import { UmbracoIndstillinger, UmbracoIndstillingerObserver } from "../umbraco-indstillinger";
import { AppShellScrollService } from "./app-shell-scroll.service";
import { AppShellSettings } from "./app-shell-settings.model";
import { RolleBodyClassService } from "../util/rolle-body-class.service";
import { MatIcon } from "@angular/material/icon";
import { DffedbDriftsstatusKnapComponent } from "../../../../web-components/src/lib/dffedb-driftsstatus-knap/dffedb-driftsstatus-knap.component";
import { EforsyningSidenavMenuComponent } from "../eforsyning-menu/eforsyning-sidenav-menu/eforsyning-sidenav-menu.component";
import { ForsyningwebSidenavMenuComponent } from "../eforsyning-menu/forsyningweb-sidenav-menu/forsyningweb-sidenav-menu.component";
import { UlaesteDokumenterService as UlaesteDokumenterService } from "../edh";
import { NotificationCenterComponent } from "../push-notification";

@Component({
    selector: "app-shell",
    templateUrl: "./app-shell.component.html",
    styleUrls: ["./app-shell.component.scss"],
    imports: [
        NgTemplateOutlet,
        MatIcon,
        DffedbDriftsstatusKnapComponent,
        MatSidenavContainer,
        MatSidenav,
        EforsyningSidenavMenuComponent,
        ForsyningwebSidenavMenuComponent,
        MatSidenavContent,
        AsyncPipe,
        NotificationCenterComponent
    ]
})
export class AppShellComponent implements AfterViewInit {
    private readonly missingImageFallbackUrl = "/assets/images/missing-image.svg";
    public settings$: Observable<AppShellSettings>;
    public indstillinger$: Observable<UmbracoIndstillinger>;
    public navbarMobileHeight$ = new Observable<number>();
    public scrollSubscription: Subscription;

    public scrollPositionTop = true;
    public fofBruger = false;

    public burgerMenuBadge$: Observable<number | null>;
    public eforsyningIcon$: Observable<string>;

    @ViewChild(MatSidenavContent)
    public set setMatSidenavContent(value: MatSidenavContent) {
        this.scrollService.init(value);

        if (this.scrollSubscription) {
            this.scrollSubscription.unsubscribe();
        }
        if (!value) return;
        this.scrollSubscription = value.elementScrolled().subscribe(() => {
            const isAtTop = value.measureScrollOffset("top") < 120;
            this.scrollPositionTop = isAtTop;
        });
    }

    @ViewChild("navbarMobile")
    public navbarMobile: ElementRef;

    constructor(
        breakpointObserver: BreakpointObserver,
        @Inject(DOCUMENT) private document: Document,
        indstillingerObserver: UmbracoIndstillingerObserver,
        private readonly aktuelBrugerObserver: AktuelBrugerObserver,
        private readonly scrollService: AppShellScrollService,
        private readonly rolleBodyClassService: RolleBodyClassService,
        private readonly ulaesteDokumenterService: UlaesteDokumenterService
    ) {
        this.indstillinger$ = indstillingerObserver.valueChanges;
        const breakpointChange$ = breakpointObserver.observe(["(min-width: 768px)"]);
        this.settings$ = combineLatest([this.indstillinger$, breakpointChange$]).pipe(
            map(([indstillinger, state]) => ({
                isDesktop: state.matches,
                isMobile: !state.matches,
                visDriftsstatus: indstillinger.visDriftsstatus,
                driftsstatusForstyrrelserTekst: indstillinger.driftsstatusForstyrrelserTekst,
                driftsstatusIngenForstyrrelserTekst: indstillinger.driftsstatusIngenForstyrrelserTekst
            }))
        );

        this.burgerMenuBadge$ = this.ulaesteDokumenterService.hentAntalUlaesteDokumenter().pipe(map((antal) => (antal > 0 ? antal : null)));
        this.eforsyningIcon$ = this.aktuelBrugerObserver.valueChanges.pipe(map((b) => (b && "lock-open") || "lock"));
    }

    public toggleAriaExpanded(id: HTMLElement, menuType: string) {
        id.attributes.getNamedItem("aria-expanded").value = id.attributes.getNamedItem("aria-expanded").value === "true" ? "false" : "true";
        id.attributes.getNamedItem("aria-label").value = id.attributes.getNamedItem("aria-expanded").value === "true" ? "Luk " + menuType : "Åben " + menuType;
    }

    public onLogoError(event: Event): void {
        const img = event?.target as HTMLImageElement | null;
        if (!img) return;

        if (img.src?.includes(this.missingImageFallbackUrl)) return;
        img.src = this.missingImageFallbackUrl;
    }

    public ngAfterViewInit(): void {
        // Ville gerne sætte højden i html til [style.top.px]="navbarMobile.offsetHeight"
        // Det giver imidlertid fejlen: ExpressionChangedAfterItHasBeenCheckedError
        this.navbarMobileHeight$ = timer(0, 100).pipe(
            map(() => this.navbarMobile.nativeElement.offsetHeight),
            distinctUntilChanged()
        );

        this.aktuelBrugerObserver.valueChanges.subscribe((b) => (this.fofBruger = (b && b.rolle && b.rolle === 9) || false));
    }
}
